/*
 * NEXZ V2 — Complete the Set (Stage F, extended post-Gate-K)
 *
 * Adds the current product's selected variant plus a chosen variant of
 * the linked partner product in a single request. Horizon's own
 * product-form.js only ever submits its own single-variant form, so it
 * has no path for a two-line add — that's the one documented gap this
 * file exists to cover (per the Stage F approval condition: no forks
 * unless a documented gap requires a minimal adapter).
 *
 * The partner's colour and size come from the chips in
 * blocks/nexz-complete-set.liquid. The current product's variant is the
 * size chosen in the page's main size picker — read from the product
 * form's `id` field at the moment "Add both" is pressed. That field is
 * empty until a size is chosen (snippets/nexz-require-choice.liquid), in
 * which case the shopper is asked to choose one; nothing is added.
 * Prices shown are each item's own, already formatted by Liquid; no
 * set saving is displayed — Shopify applies any discount in the cart.
 *
 * Fix (Oct 2026): the partner photo is a responsive <img> with srcset.
 * Changing only `src` left the browser on the old srcset candidate, so
 * the photo kept showing the first colour after another was picked.
 * The swap now rebuilds srcset from data-partner-image-widths.
 *
 * Everything cart-facing is reused, not reinvented: Theme.routes.cart_add_url
 * (the same global the rest of the theme uses, defined in
 * snippets/scripts.liquid) and fetchConfig from @theme/utilities (the
 * same helper product-form.js itself uses to build the request). The
 * dispatched event is a real CartLinesUpdateEvent from @shopify/events,
 * so the cart drawer, header cart count, and this file's own analytics
 * listener in nexz-pdp.js all observe the same event shape a native add
 * would produce — this is not a second, forked cart-update signal.
 */
import { fetchConfig } from '@theme/utilities';
import { CartLinesUpdateEvent } from '@shopify/events';

class NexzCompleteSet extends HTMLElement {
  connectedCallback() {
    this.#button = this.querySelector('[data-nexz-set-add]');
    this.#partnerPrice = this.querySelector('[data-nexz-set-partner-price]');
    this.#errorEl = this.querySelector('[data-nexz-set-error]');
    this.#labelEl = this.querySelector('[data-nexz-set-add-label]');
    this.#partnerLink = this.querySelector('[data-nexz-set-partner-link]');
    this.#partnerName = this.querySelector('[data-nexz-set-partner-name]');

    if (!this.#button) return;

    this.querySelectorAll('[data-nexz-set-color]').forEach((radio) => {
      radio.addEventListener('change', () => this.#handleColorChange(radio));
    });

    this.querySelectorAll('[data-nexz-set-size]').forEach((radio) => {
      radio.addEventListener('change', () => this.#handleSizeChange(radio));
    });

    this.#button.addEventListener('click', () => this.#handleAdd());
  }

  /** @param {HTMLInputElement} radio */
  #handleColorChange(radio) {
    const colorId = radio.value;

    this.querySelectorAll('[data-nexz-set-size-group]').forEach((group) => {
      group.hidden = group.dataset.colorId !== colorId;
    });

    // Switching color invalidates any size chosen under the previous
    // color — re-lock the gate rather than silently keeping a variant
    // id from a group that's no longer visible.
    this.querySelectorAll('[data-nexz-set-size]').forEach((sizeRadio) => {
      sizeRadio.checked = false;
    });
    this.#setGated(true);

    if (this.#partnerLink) this.#partnerLink.href = radio.dataset.partnerUrl || this.#partnerLink.href;
    if (this.#partnerName) {
      this.#partnerName.href = radio.dataset.partnerUrl || this.#partnerName.href;
      if (radio.dataset.partnerTitle) this.#partnerName.textContent = radio.dataset.partnerTitle;
    }
    if (this.#partnerPrice && radio.dataset.partnerPrice) this.#partnerPrice.textContent = radio.dataset.partnerPrice;
    if (this.#partnerLink && radio.dataset.partnerImageSrc) {
      const img = this.#partnerLink.querySelector('img');
      if (img) this.#swapImage(img, radio.dataset.partnerImageSrc, radio.dataset.partnerImageWidths, radio.dataset.partnerTitle);
    }
  }

  /**
   * Point a responsive <img> at a new Shopify CDN image. Rebuilds srcset
   * for the given widths (the browser prefers srcset over src, so a
   * src-only change is ignored); with no widths, drops srcset instead.
   * @param {HTMLImageElement} img
   * @param {string} src
   * @param {string | undefined} widths - e.g. "80,160,240"
   * @param {string | undefined} alt
   */
  #swapImage(img, src, widths, alt) {
    const list = (widths || '')
      .split(',')
      .map((w) => parseInt(w.trim(), 10))
      .filter((w) => w > 0);

    if (list.length) {
      const srcset = list
        .map((w) => {
          try {
            const url = new URL(src, window.location.href);
            url.searchParams.set('width', String(w));
            return `${url.toString()} ${w}w`;
          } catch (e) {
            return '';
          }
        })
        .filter(Boolean)
        .join(', ');
      if (srcset) {
        img.setAttribute('srcset', srcset);
      } else {
        img.removeAttribute('srcset');
      }
    } else {
      img.removeAttribute('srcset');
    }

    img.src = src;
    if (alt) img.alt = alt;
  }

  /** @param {HTMLInputElement} radio */
  #handleSizeChange(radio) {
    if (!radio.checked) return;
    this.#setGated(false);
  }

  /** @param {boolean} gated */
  #setGated(gated) {
    if (!this.#button) return;
    if (gated) {
      this.#button.setAttribute('aria-disabled', 'true');
      if (this.#labelEl) this.#labelEl.textContent = this.#button.dataset.selectSizeLabel || this.#labelEl.textContent;
    } else {
      this.#button.removeAttribute('aria-disabled');
      if (this.#labelEl) this.#labelEl.textContent = this.#button.dataset.addLabel || this.#labelEl.textContent;
    }
  }

  #handleAdd() {
    if (!this.#button || this.#button.getAttribute('aria-disabled') === 'true') return;

    // The size chosen in the main picker. Empty until one is chosen.
    const mainForm = document.querySelector('form[data-type="add-to-cart-form"]');
    const currentVariantId = mainForm && mainForm.elements.namedItem('id') ? mainForm.elements.namedItem('id').value : '';
    if (!currentVariantId) {
      if (this.#errorEl) {
        this.#errorEl.textContent = this.dataset.chooseMainSizeLabel || '';
        this.#errorEl.hidden = false;
      }
      const mainSizes = document.querySelector('variant-picker fieldset');
      if (mainSizes) {
        mainSizes.scrollIntoView({ block: 'center' });
        const first = mainSizes.querySelector('input[type="radio"]:not([data-option-available="false"])');
        if (first) first.focus({ preventScroll: true });
      }
      return;
    }
    const sizeRadio = /** @type {HTMLInputElement | null} */ (this.querySelector('[data-nexz-set-size]:checked'));
    const partnerVariantId = sizeRadio ? sizeRadio.value : null;
    // The actually-added colorway, not necessarily the originally-linked
    // set_partner — the shopper may have switched color before adding.
    // Falls back to the static default for the no-color-picker case
    // (partner has no sibling colorways, so no [data-nexz-set-color] exists).
    const colorRadio = /** @type {HTMLInputElement | null} */ (this.querySelector('[data-nexz-set-color]:checked'));
    const partnerProductId = colorRadio ? colorRadio.value : this.dataset.partnerProductId;
    if (!currentVariantId || !partnerVariantId) return;

    this.#button.setAttribute('aria-disabled', 'true');
    if (this.#errorEl) this.#errorEl.hidden = true;

    const lines = [
      { merchandiseId: `gid://shopify/ProductVariant/${currentVariantId}`, quantity: 1 },
      { merchandiseId: `gid://shopify/ProductVariant/${partnerVariantId}`, quantity: 1 },
    ];

    let resolvePromise, rejectPromise;
    const deferredPromise = new Promise((resolve, reject) => {
      resolvePromise = resolve;
      rejectPromise = reject;
    });
    // Handled here as well as by cart-drawer.js — see nexz-cart-undo.js for
    // the rationale. The inline error element below stays the user-facing
    // report; this only stops an unhandled rejection reaching monitoring.
    deferredPromise.catch(() => {});

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'set',
        lines,
        promise: deferredPromise,
      })
    );

    const fetchCfg = fetchConfig('json', {
      body: JSON.stringify({
        items: [
          { id: Number(currentVariantId), quantity: 1 },
          { id: Number(partnerVariantId), quantity: 1 },
        ],
      }),
    });

    fetch(Theme.routes.cart_add_url, fetchCfg)
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          const message = response.description || response.message || this.#button?.dataset.errorLabel || '';
          rejectPromise(new Error(message));
          if (this.#errorEl) {
            this.#errorEl.hidden = false;
            this.#errorEl.textContent = message;
          }
          return;
        }
        resolvePromise({ cart: response, detail: { didError: false } });
        this.dispatchEvent(
          new CustomEvent('nexz:set_add', {
            bubbles: true,
            detail: { productId: partnerProductId, currentVariantId, partnerVariantId },
          })
        );
        document.dispatchEvent(
          new CustomEvent('nexz:set_add', {
            bubbles: true,
            detail: { productId: partnerProductId, currentVariantId, partnerVariantId },
          })
        );
        window.location.href = Theme.routes.cart_url;
      })
      .catch((error) => {
        rejectPromise(error);
        if (this.#errorEl) {
          this.#errorEl.hidden = false;
          this.#errorEl.textContent = this.#button?.dataset.errorLabel || '';
        }
      })
      .finally(() => {
        this.#button.removeAttribute('aria-disabled');
      });
  }

  /** @type {HTMLButtonElement | null} */
  #button = null;
  /** @type {HTMLElement | null} */
  #partnerPrice = null;
  /** @type {HTMLElement | null} */
  /** @type {HTMLElement | null} */
  #errorEl = null;
  /** @type {HTMLElement | null} */
  #labelEl = null;
  /** @type {HTMLAnchorElement | null} */
  #partnerLink = null;
  /** @type {HTMLAnchorElement | null} */
  #partnerName = null;
}

if (!customElements.get('nexz-complete-set')) {
  customElements.define('nexz-complete-set', NexzCompleteSet);
}
